import { useEffect, useCallback } from 'react'
import { initializeApp, getApps } from 'firebase/app'
import { getAnalytics, logEvent, setUserProperties, setUserId } from 'firebase/analytics'
import { getPerformance, trace } from 'firebase/performance'

/**
 * Configuración de Firebase
 * @see https://firebase.google.com/docs/web/setup
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

let analytics = null
let performance = null

/**
 * Inicializa Firebase Analytics y Performance
 * @returns {Object|null} Instancia de Analytics o null si no está disponible
 */
function initializeFirebaseAnalytics() {
  // Solo inicializar en el navegador y si hay configuración
  if (typeof window === 'undefined' || !firebaseConfig.apiKey) {
    // Sentry removido
    return null
  }

  // Validar que todas las credenciales necesarias estén presentes
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'appId']
  const missingFields = requiredFields.filter(field => !firebaseConfig[field])

  if (missingFields.length > 0) {
    // Sentry removido
    return null
  }

  try {
    // Inicializar app si no existe
    const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0]

    // Inicializar Analytics
    if (!analytics) {
      analytics = getAnalytics(app)
      // Sentry removido
    }

    // Inicializar Performance
    if (!performance) {
      performance = getPerformance(app)
      // Sentry removido
    }

    return analytics
  } catch {
    // Registrar error pero no lanzarlo
    // Sentry removido

    // Marcar analytics como null para que todos los métodos fallen silenciosamente
    analytics = null
    return null
  }
}

/**
 * @typedef {Object} FirebaseAnalyticsAPI
 *
 * @property {(eventName: string, params?: Object) => void} trackEvent
 * Registra un evento personalizado en Google Analytics 4.
 * @see https://firebase.google.com/docs/analytics/events?platform=web
 * @example
 * analytics.trackEvent('button_click', { button_name: 'cta', location: 'header' })
 *
 * @property {(pagePath: string, pageTitle?: string) => void} trackPageView
 * Registra una vista de página con ruta y título opcionales.
 * Automáticamente incluye page_location y page_title.
 * @see https://firebase.google.com/docs/analytics/events?platform=web#page_view
 * @example
 * analytics.trackPageView('/ar-scene', 'Escena AR')
 *
 * @property {(elementName: string, params?: Object) => void} trackClick
 * Registra un clic en un elemento específico.
 * @see https://firebase.google.com/docs/analytics/events?platform=web#select_content
 * @example
 * analytics.trackClick('menu_button', { location: 'sidebar' })
 *
 * @property {(searchTerm: string) => void} trackSearch
 * Registra una búsqueda realizada por el usuario.
 * @see https://firebase.google.com/docs/analytics/events?platform=web#search
 * @example
 * analytics.trackSearch('manzana verde')
 *
 * @property {(contentType: string, contentId: string, method: string) => void} trackShare
 * Registra cuando el usuario comparte contenido.
 * @see https://firebase.google.com/docs/analytics/events?platform=web#share
 * @example
 * analytics.trackShare('ar_scene', 'scene_123', 'whatsapp')
 *
 * @property {(errorMessage: string, params?: Object) => void} trackError
 * Registra un error con contexto adicional.
 * @example
 * analytics.trackError('Camera permission denied', { errorType: 'permission', fatal: true })
 *
 * @property {(params?: Object) => void} trackARSessionStart
 * Registra el inicio de una sesión de realidad aumentada.
 * @example
 * analytics.trackARSessionStart({ marker_type: 'image', device_type: 'mobile' })
 *
 * @property {(duration: number, params?: Object) => void} trackARSessionEnd
 * Registra el fin de una sesión AR con duración en milisegundos.
 * @example
 * analytics.trackARSessionEnd(120000, { markers_detected: 3, successful: true })
 *
 * @property {(markerId: number|string, params?: Object) => void} trackMarkerDetected
 * Registra cuando se detecta un marcador AR.
 * @example
 * analytics.trackMarkerDetected(0, { first_detection: true, detection_time: 245.67 })
 *
 * @property {(markerId: number|string, trackingDuration?: number) => void} trackMarkerLost
 * Registra cuando se pierde el tracking de un marcador.
 * @example
 * analytics.trackMarkerLost(0, 5000)
 *
 * @property {(interactionType: string, params?: Object) => void} trackARInteraction
 * Registra interacciones del usuario con contenido AR.
 * @example
 * analytics.trackARInteraction('rotate', { object_id: 'model_123', rotation_angle: 45 })
 *
 * @property {() => void} trackARScreenshot
 * Registra cuando el usuario toma un screenshot de la escena AR.
 * @example
 * analytics.trackARScreenshot()
 *
 * @property {(reason: string) => void} trackCameraPermissionError
 * Registra errores relacionados con permisos de cámara.
 * @example
 * analytics.trackCameraPermissionError('User denied permission')
 *
 * @property {(metric: string, value: number) => void} trackPerformanceIssue
 * Registra problemas de rendimiento con métricas específicas.
 * @example
 * analytics.trackPerformanceIssue('fps', 25)
 *
 * @property {(traceName: string) => Object} createPerformanceTrace
 * Crea un trace de Firebase Performance Monitoring para medir operaciones personalizadas.
 * Retorna un objeto con métodos: start(), stop(), putAttribute(key, value), putMetric(key, value).
 * @example
 * const trace = analytics.createPerformanceTrace('load_3d_model')
 * trace.start()
 * // ... código a medir ...
 * trace.putAttribute('model_type', 'gltf')
 * trace.putMetric('vertices_count', 1024)
 * trace.stop()
 *
 * @property {(params: Object) => void} track3DObjectLoadStart
 * Registra inicio de carga de objeto 3D.
 * @example
 * analytics.track3DObjectLoadStart({ object_id: 'plane_1', object_type: 'plane', geometry_type: 'PlaneGeometry' })
 *
 * @property {(params: Object) => void} track3DObjectLoadEnd
 * Registra finalización de carga de objeto 3D con métricas de performance.
 * @example
 * analytics.track3DObjectLoadEnd({ object_id: 'plane_1', object_type: 'plane', load_time_ms: 245.67, success: true, vertices_count: 4, faces_count: 2 })
 *
 * @property {(params: Object) => void} track3DObjectLoadError
 * Registra error al cargar objeto 3D.
 * @example
 * analytics.track3DObjectLoadError({ object_id: 'model_1', object_type: 'gltf', error_message: 'Failed to load texture', load_time_ms: 1200 })
 *
 * @property {() => void} trackTutorialBegin
 * Registra cuando el usuario inicia un tutorial.
 * @see https://firebase.google.com/docs/analytics/events?platform=web#tutorial_begin
 * @example
 * analytics.trackTutorialBegin()
 *
 * @property {() => void} trackTutorialComplete
 * Registra cuando el usuario completa un tutorial.
 * @see https://firebase.google.com/docs/analytics/events?platform=web#tutorial_complete
 * @example
 * analytics.trackTutorialComplete()
 *
 * @property {(properties: Object) => void} setUserProps
 * Configura propiedades personalizadas del usuario que persisten entre sesiones.
 * Útil para segmentación y análisis de cohortes.
 * Propiedades recomendadas: language, timezone, device_type, app_version, subscription_tier.
 * @see https://firebase.google.com/docs/analytics/user-properties?platform=web
 * @example
 * analytics.setUserProps({
 *   language: navigator.language,
 *   timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
 *   device_type: 'mobile',
 *   ar_capable: true,
 *   webgl_support: 'webgl2'
 * })
 *
 * @property {(userId: string) => void} setUser
 * Configura el ID único del usuario para tracking cross-device.
 * @see https://firebase.google.com/docs/analytics/userid?platform=web
 * @example
 * analytics.setUser('user_12345')
 */

/**
 * Hook para Firebase Analytics (GA4)
 *
 * Proporciona una API completa para registrar eventos, propiedades de usuario
 * y métricas personalizadas en Google Analytics 4 via Firebase.
 *
 * Inicializa automáticamente Firebase Analytics y proporciona métodos
 * optimizados para aplicaciones de realidad aumentada.
 *
 * @returns {FirebaseAnalyticsAPI} API de Firebase Analytics con métodos para tracking
 * @see https://firebase.google.com/docs/analytics/get-started?platform=web
 * @see https://firebase.google.com/docs/reference/js/analytics
 *
 * @example
 * // Uso básico
 * function App() {
 *   const analytics = useFirebaseAnalytics()
 *
 *   useEffect(() => {
 *     analytics.trackPageView('/', 'Home')
 *     analytics.setUserProps({
 *       language: navigator.language,
 *       device_type: 'mobile'
 *     })
 *   }, [analytics])
 * }
 *
 * @example
 * // Tracking de eventos AR
 * function ARScene() {
 *   const analytics = useFirebaseAnalytics()
 *
 *   const handleMarkerFound = (markerId) => {
 *     analytics.trackMarkerDetected(markerId, {
 *       first_detection: true,
 *       detection_time: 245.67
 *     })
 *   }
 * }
 */
export function useFirebaseAnalytics() {
  useEffect(() => {
    initializeFirebaseAnalytics()
  }, [])

  /**
   * Registra un evento personalizado
   * @param {string} eventName - Nombre del evento
   * @param {Object} [params] - Parámetros del evento
   */
  const trackEvent = useCallback((eventName, params = {}) => {
    // Fallar silenciosamente si Firebase no está disponible
    if (!analytics) {
      // Sentry removido
      return
    }

    try {
      logEvent(analytics, eventName, {
        ...params,
        timestamp: new Date().toISOString(),
      })
    } catch {
      // No lanzar error, solo registrar warning
      // Sentry removido
    }
  }, [])

  /**
   * Registra vista de página
   * @param {string} pagePath - Ruta de la página
   * @param {string} [pageTitle] - Título de la página
   */
  const trackPageView = useCallback(
    (pagePath, pageTitle) => {
      trackEvent('page_view', {
        page_path: pagePath,
        page_title: pageTitle || document.title,
        page_location: window.location.href,
      })
    },
    [trackEvent]
  )

  /**
   * Registra inicio de sesión AR
   * @param {Object} [params] - Parámetros adicionales
   */
  const trackARSessionStart = useCallback(
    (params = {}) => {
      trackEvent('ar_session_start', {
        session_type: 'ar',
        ...params,
      })
    },
    [trackEvent]
  )

  /**
   * Registra finalización de sesión AR
   * @param {number} duration - Duración en ms
   * @param {Object} [params] - Parámetros adicionales
   */
  const trackARSessionEnd = useCallback(
    (duration, params = {}) => {
      trackEvent('ar_session_end', {
        session_duration: duration,
        session_duration_seconds: Math.round(duration / 1000),
        ...params,
      })
    },
    [trackEvent]
  )

  /**
   * Registra detección de marcador AR
   * @param {number|string} markerId - ID del marcador
   * @param {Object} [params] - Parámetros adicionales
   */
  const trackMarkerDetected = useCallback(
    (markerId, params = {}) => {
      trackEvent('ar_marker_detected', {
        marker_id: markerId,
        ...params,
      })
    },
    [trackEvent]
  )

  /**
   * Registra pérdida de marcador AR
   * @param {number|string} markerId - ID del marcador
   * @param {number} [trackingDuration] - Duración del tracking en ms
   */
  const trackMarkerLost = useCallback(
    (markerId, trackingDuration) => {
      trackEvent('ar_marker_lost', {
        marker_id: markerId,
        tracking_duration: trackingDuration,
        tracking_duration_seconds: trackingDuration ? Math.round(trackingDuration / 1000) : 0,
      })
    },
    [trackEvent]
  )

  /**
   * Registra error
   * @param {string} errorMessage - Mensaje de error
   * @param {Object} [params] - Contexto adicional
   */
  const trackError = useCallback(
    (errorMessage, params = {}) => {
      trackEvent('error', {
        error_message: errorMessage,
        error_type: params.errorType || 'unknown',
        error_fatal: params.fatal || false,
        ...params,
      })
    },
    [trackEvent]
  )

  /**
   * Registra clic en elemento
   * @param {string} elementName - Nombre del elemento
   * @param {Object} [params] - Parámetros adicionales
   */
  const trackClick = useCallback(
    (elementName, params = {}) => {
      trackEvent('click', {
        element_name: elementName,
        ...params,
      })
    },
    [trackEvent]
  )

  /**
   * Registra búsqueda
   * @param {string} searchTerm - Término de búsqueda
   */
  const trackSearch = useCallback(
    searchTerm => {
      trackEvent('search', {
        search_term: searchTerm,
      })
    },
    [trackEvent]
  )

  /**
   * Registra compartir contenido
   * @param {string} contentType - Tipo de contenido
   * @param {string} contentId - ID del contenido
   * @param {string} method - Método de compartir
   */
  const trackShare = useCallback(
    (contentType, contentId, method) => {
      trackEvent('share', {
        content_type: contentType,
        content_id: contentId,
        method: method,
      })
    },
    [trackEvent]
  )

  /**
   * Registra inicio de tutorial
   */
  const trackTutorialBegin = useCallback(() => {
    trackEvent('tutorial_begin')
  }, [trackEvent])

  /**
   * Registra completación de tutorial
   */
  const trackTutorialComplete = useCallback(() => {
    trackEvent('tutorial_complete')
  }, [trackEvent])

  /**
   * Registra interacción con contenido AR
   * @param {string} interactionType - Tipo de interacción
   * @param {Object} [params] - Parámetros adicionales
   */
  const trackARInteraction = useCallback(
    (interactionType, params = {}) => {
      trackEvent('ar_interaction', {
        interaction_type: interactionType,
        ...params,
      })
    },
    [trackEvent]
  )

  /**
   * Registra captura de screenshot AR
   */
  const trackARScreenshot = useCallback(() => {
    trackEvent('ar_screenshot_taken', {
      feature: 'screenshot',
    })
  }, [trackEvent])

  /**
   * Registra error de permisos de cámara
   * @param {string} reason - Razón del error
   */
  const trackCameraPermissionError = useCallback(
    reason => {
      trackEvent('camera_permission_denied', {
        error_type: 'permission',
        permission_type: 'camera',
        reason: reason || 'unknown',
      })
    },
    [trackEvent]
  )

  /**
   * Registra problema de rendimiento
   * @param {string} metric - Métrica afectada (fps, latency, memory)
   * @param {number} value - Valor de la métrica
   */
  const trackPerformanceIssue = useCallback(
    (metric, value) => {
      trackEvent('performance_issue', {
        metric_name: metric,
        metric_value: value,
        severity: value < 30 ? 'critical' : value < 45 ? 'warning' : 'info',
      })
    },
    [trackEvent]
  )

  /**
   * Crea un trace de performance para medir operaciones
   * @param {string} traceName - Nombre del trace
   * @returns {Object|null} Objeto trace con métodos start(), stop(), putAttribute(), putMetric()
   */
  const createPerformanceTrace = useCallback(traceName => {
    if (!performance) {
      // Sentry removido
      // Retornar objeto mock para que el código no falle
      return {
        start: () => {},
        stop: () => {},
        putAttribute: () => {},
        putMetric: () => {},
        incrementMetric: () => {},
      }
    }

    try {
      return trace(performance, traceName)
    } catch {
      // Sentry removido
      // Retornar objeto mock
      return {
        start: () => {},
        stop: () => {},
        putAttribute: () => {},
        putMetric: () => {},
        incrementMetric: () => {},
      }
    }
  }, [])

  /**
   * Registra inicio de carga de objeto 3D
   * @param {Object} params - Parámetros del objeto
   * @param {string} params.object_id - ID del objeto
   * @param {string} params.object_type - Tipo de objeto (plane, model, mesh)
   * @param {string} params.geometry_type - Tipo de geometría (PlaneGeometry, BoxGeometry, etc)
   */
  const track3DObjectLoadStart = useCallback(
    params => {
      trackEvent('ar_3d_object_load_start', {
        ...params,
      })
    },
    [trackEvent]
  )

  /**
   * Registra finalización de carga de objeto 3D
   * @param {Object} params - Parámetros del objeto y performance
   * @param {string} params.object_id - ID del objeto
   * @param {string} params.object_type - Tipo de objeto
   * @param {number} params.load_time_ms - Tiempo de carga en milisegundos
   * @param {boolean} params.success - Si la carga fue exitosa
   * @param {number} [params.vertices_count] - Cantidad de vértices
   * @param {number} [params.faces_count] - Cantidad de caras
   * @param {number} [params.file_size_kb] - Tamaño del archivo en KB
   */
  const track3DObjectLoadEnd = useCallback(
    params => {
      trackEvent('ar_3d_object_load_complete', {
        ...params,
        load_time_seconds: (params.load_time_ms / 1000).toFixed(2),
        load_speed:
          params.load_time_ms < 100
            ? 'fast'
            : params.load_time_ms < 500
              ? 'normal'
              : params.load_time_ms < 2000
                ? 'slow'
                : 'very_slow',
      })
    },
    [trackEvent]
  )

  /**
   * Registra error al cargar objeto 3D
   * @param {Object} params - Parámetros del error
   * @param {string} params.object_id - ID del objeto
   * @param {string} params.object_type - Tipo de objeto
   * @param {string} params.error_message - Mensaje de error
   * @param {number} [params.load_time_ms] - Tiempo hasta el error
   */
  const track3DObjectLoadError = useCallback(
    params => {
      trackEvent('ar_3d_object_load_error', {
        ...params,
        error_type: '3d_loading',
      })
    },
    [trackEvent]
  )

  /**
   * Configura propiedades de usuario con información enriquecida del navegador y dispositivo
   * @param {Object} properties - Propiedades del usuario
   * @param {string} [properties.app_version] - Versión de la aplicación
   * @param {string} [properties.device_type] - Tipo de dispositivo (mobile/tablet/desktop)
   * @param {string} [properties.device_brand] - Marca del dispositivo
   * @param {string} [properties.device_model] - Modelo del dispositivo
   * @param {string} [properties.browser] - Navegador (chrome, firefox, safari, etc.)
   * @param {string} [properties.browser_version] - Versión del navegador
   * @param {string} [properties.os] - Sistema operativo
   * @param {string} [properties.language] - Idioma del navegador
   * @param {string} [properties.timezone] - Zona horaria
   * @param {string} [properties.screen_resolution] - Resolución de pantalla
   * @param {boolean} [properties.webgl_support] - Soporte de WebGL
   * @param {boolean} [properties.ar_capable] - Capacidad AR
   * @param {string} [properties.connection_type] - Tipo de conexión
   * @param {boolean} [properties.cookies_enabled] - Cookies habilitadas
   *
   * @example
   * ```js
   * analytics.setUserProps({
   *   app_version: '1.0.0',
   *   device_type: 'mobile',
   *   language: 'es-ES',
   *   ar_capable: true
   * })
   * ```
   */
  const setUserProps = useCallback((properties = {}) => {
    // Fallar silenciosamente si Firebase no está disponible
    if (!analytics) {
      // Sentry removido
      return
    }

    try {
      // Propiedades enriquecidas automáticamente
      const enrichedProps = {
        // Propiedades del usuario
        ...properties,

        // Idioma y localización
        language: properties.language || navigator.language || 'unknown',
        languages: navigator.languages ? navigator.languages.join(',') : 'unknown',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
        timezone_offset: new Date().getTimezoneOffset(),

        // Sistema operativo
        os:
          properties.os ||
          (() => {
            const ua = navigator.userAgent
            if (ua.includes('Win')) return 'Windows'
            if (ua.includes('Mac')) return 'MacOS'
            if (ua.includes('Linux')) return 'Linux'
            if (ua.includes('Android')) return 'Android'
            if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS'
            return 'unknown'
          })(),

        // Pantalla
        screen_resolution:
          properties.screen_resolution || `${window.screen.width}x${window.screen.height}`,
        viewport_resolution: `${window.innerWidth}x${window.innerHeight}`,
        device_pixel_ratio: window.devicePixelRatio || 1,
        color_depth: window.screen.colorDepth || 'unknown',

        // Capacidades
        cookies_enabled:
          properties.cookies_enabled !== undefined
            ? properties.cookies_enabled
            : navigator.cookieEnabled,
        local_storage_enabled: (() => {
          try {
            return !!window.localStorage
          } catch {
            return false
          }
        })(),
        service_worker_supported: 'serviceWorker' in navigator,
        notification_permission:
          'Notification' in window ? Notification.permission : 'not_supported',

        // Conexión
        connection_type:
          properties.connection_type || navigator.connection?.effectiveType || 'unknown',
        save_data_mode: navigator.connection?.saveData || false,

        // Hardware
        device_memory_gb: navigator.deviceMemory || 'unknown',
        hardware_concurrency: navigator.hardwareConcurrency || 'unknown',
        max_touch_points: navigator.maxTouchPoints || 0,

        // Marca de tiempo
        first_visit: properties.first_visit || new Date().toISOString(),
        last_updated: new Date().toISOString(),
      }

      setUserProperties(analytics, enrichedProps)
    } catch {
      // No lanzar error, solo registrar warning
      // Sentry removido
    }
  }, [])

  /**
   * Configura ID de usuario
   * @param {string} userId - ID del usuario
   */
  const setUser = useCallback(userId => {
    // Fallar silenciosamente si Firebase no está disponible
    if (!analytics) {
      // Sentry removido
      return
    }

    try {
      setUserId(analytics, userId)
    } catch {
      // No lanzar error, solo registrar warning
      // Sentry removido
    }
  }, [])

  /**
   * API de Firebase Analytics con todos los métodos de tracking disponibles
   * @type {FirebaseAnalyticsAPI}
   */
  const analyticsAPI = {
    // Eventos generales

    /**
     * Registra un evento personalizado en Google Analytics 4
     * @see https://firebase.google.com/docs/analytics/events?platform=web
     * @example analytics.trackEvent('button_click', { button_name: 'cta', location: 'header' })
     */
    trackEvent,

    /**
     * Registra una vista de página con ruta y título opcionales
     * @see https://firebase.google.com/docs/analytics/events?platform=web#page_view
     * @example analytics.trackPageView('/ar-scene', 'Escena AR')
     */
    trackPageView,

    /**
     * Registra un clic en un elemento específico
     * @see https://firebase.google.com/docs/analytics/events?platform=web#select_content
     * @example analytics.trackClick('menu_button', { location: 'sidebar' })
     */
    trackClick,

    /**
     * Registra una búsqueda realizada por el usuario
     * @see https://firebase.google.com/docs/analytics/events?platform=web#search
     * @example analytics.trackSearch('manzana verde')
     */
    trackSearch,

    /**
     * Registra cuando el usuario comparte contenido
     * @see https://firebase.google.com/docs/analytics/events?platform=web#share
     * @example analytics.trackShare('ar_scene', 'scene_123', 'whatsapp')
     */
    trackShare,

    /**
     * Registra un error con contexto adicional
     * @example analytics.trackError('Camera permission denied', { errorType: 'permission', fatal: true })
     */
    trackError,

    // Eventos AR específicos

    /**
     * Registra el inicio de una sesión de realidad aumentada
     * @example analytics.trackARSessionStart({ marker_type: 'image', device_type: 'mobile' })
     */
    trackARSessionStart,

    /**
     * Registra el fin de una sesión AR con duración en milisegundos
     * @example analytics.trackARSessionEnd(120000, { markers_detected: 3, successful: true })
     */
    trackARSessionEnd,

    /**
     * Registra cuando se detecta un marcador AR
     * @example analytics.trackMarkerDetected(0, { first_detection: true, detection_time: 245.67 })
     */
    trackMarkerDetected,

    /**
     * Registra cuando se pierde el tracking de un marcador
     * @example analytics.trackMarkerLost(0, 5000)
     */
    trackMarkerLost,

    /**
     * Registra interacciones del usuario con contenido AR
     * @example analytics.trackARInteraction('rotate', { object_id: 'model_123', rotation_angle: 45 })
     */
    trackARInteraction,

    /**
     * Registra cuando el usuario toma un screenshot de la escena AR
     * @example analytics.trackARScreenshot()
     */
    trackARScreenshot,

    /**
     * Registra errores relacionados con permisos de cámara
     * @example analytics.trackCameraPermissionError('User denied permission')
     */
    trackCameraPermissionError,

    /**
     * Registra problemas de rendimiento con métricas específicas
     * @example analytics.trackPerformanceIssue('fps', 25)
     */
    trackPerformanceIssue,

    // Firebase Performance Monitoring

    /**
     * Crea un trace de Firebase Performance para medir operaciones personalizadas
     * @example
     * const trace = analytics.createPerformanceTrace('load_3d_model')
     * trace.start()
     * // ... operación ...
     * trace.putAttribute('model_type', 'gltf')
     * trace.putMetric('file_size_kb', 1024)
     * trace.stop()
     */
    createPerformanceTrace,

    // Eventos de carga de objetos 3D

    /**
     * Registra inicio de carga de objeto 3D
     * @example analytics.track3DObjectLoadStart({ object_id: 'plane_1', object_type: 'plane', geometry_type: 'PlaneGeometry' })
     */
    track3DObjectLoadStart,

    /**
     * Registra finalización de carga de objeto 3D con métricas de performance
     * @example analytics.track3DObjectLoadEnd({ object_id: 'plane_1', object_type: 'plane', load_time_ms: 245.67, success: true })
     */
    track3DObjectLoadEnd,

    /**
     * Registra error al cargar objeto 3D
     * @example analytics.track3DObjectLoadError({ object_id: 'model_1', object_type: 'gltf', error_message: 'Failed to load texture' })
     */
    track3DObjectLoadError,

    // Eventos de tutorial

    /**
     * Registra cuando el usuario inicia un tutorial
     * @see https://firebase.google.com/docs/analytics/events?platform=web#tutorial_begin
     * @example analytics.trackTutorialBegin()
     */
    trackTutorialBegin,

    /**
     * Registra cuando el usuario completa un tutorial
     * @see https://firebase.google.com/docs/analytics/events?platform=web#tutorial_complete
     * @example analytics.trackTutorialComplete()
     */
    trackTutorialComplete,

    // Configuración de usuario

    /**
     * Configura propiedades personalizadas del usuario que persisten entre sesiones.
     * Enriquece automáticamente con 25+ propiedades del navegador y dispositivo.
     * @see https://firebase.google.com/docs/analytics/user-properties?platform=web
     * @example analytics.setUserProps({ app_version: '1.0.0', device_type: 'mobile', ar_capable: true })
     */
    setUserProps,

    /**
     * Configura el ID único del usuario para tracking cross-device
     * @see https://firebase.google.com/docs/analytics/userid?platform=web
     * @example analytics.setUser('user_12345')
     */
    setUser,
  }

  return analyticsAPI
}

export default useFirebaseAnalytics
